import type { ColumnType } from '../types/columnType';

const MAX_SAMPLE_SIZE = 1000;

// Column name hints — if the name contains these keywords, bias toward that type
const NAME_HINTS: { pattern: RegExp; type: ColumnType }[] = [
  { pattern: /\b(url|link|href|website|endpoint)\b/i, type: 'URL' },
  { pattern: /\b(date|created|updated|published|timestamp|_at|_on)\b/i, type: 'Date' },
  { pattern: /\b(datetime|created_at|updated_at|timestamp)\b/i, type: 'DateTime' },
  { pattern: /\b(percent|pct|ratio|rate)\b/i, type: 'Percentage' },
  { pattern: /\b(price|cost|amount|revenue|salary|fee|total|budget|currency)\b/i, type: 'Currency' },
  { pattern: /\b(is_|has_|can_|enabled|active|flag|boolean|bool)\b/i, type: 'Boolean' },
  { pattern: /\b(count|num|number|qty|quantity|age|score|rank|index|size|length|width|height|weight|volume|id)\b/i, type: 'Number' },
];

function getNameHint(columnName: string): ColumnType | null {
  for (const hint of NAME_HINTS) {
    if (hint.pattern.test(columnName)) {
      return hint.type;
    }
  }
  return null;
}

function isBoolean(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return ['true', 'false', 'yes', 'no', '1', '0'].includes(normalized);
}

function isURL(value: string): boolean {
  const trimmed = value.trim();
  return /^(https?:\/\/|www\.)[^\s]+$/i.test(trimmed);
}

function isDateTime(value: string): boolean {
  const trimmed = value.trim();
  // ISO 8601 with time component
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed)) return true;
  // MM/DD/YYYY HH:MM or DD/MM/YYYY HH:MM
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}\s+\d{1,2}:\d{2}/.test(trimmed)) return true;
  return false;
}

function isDate(value: string): boolean {
  const trimmed = value.trim();
  // ISO date: 2024-01-15
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return true;
  // MM/DD/YYYY or DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(trimmed)) return true;
  // Month name formats: Jan 15, 2024 or 15 Jan 2024
  if (/^[A-Za-z]{3,}\s+\d{1,2},?\s+\d{2,4}$/.test(trimmed)) return true;
  if (/^\d{1,2}\s+[A-Za-z]{3,}\s+\d{2,4}$/.test(trimmed)) return true;
  // DD.MM.YYYY
  if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(trimmed)) return true;
  return false;
}

function isPercentage(value: string): boolean {
  const trimmed = value.trim();
  // Ends with %: "85%", "3.14%"
  if (/^-?\d+([.,]\d+)?%$/.test(trimmed)) return true;
  // Contains "percent"
  if (/^-?\d+([.,]\d+)?\s*percent$/i.test(trimmed)) return true;
  return false;
}

function isCurrency(value: string): boolean {
  const trimmed = value.trim();
  // Starts with currency symbol: $1,200 or €500
  if (/^[$€£¥₹₽]\s*-?\d/.test(trimmed)) return true;
  // Ends with currency code: 1200 USD, 500 EUR
  if (/^-?[\d,. ]+\s*[A-Z]{3}$/.test(trimmed)) return true;
  // Starts with currency code: USD 1200, EUR 500
  if (/^[A-Z]{3}\s+-?[\d,. ]+$/.test(trimmed)) return true;
  return false;
}

function isNumber(value: string): boolean {
  const trimmed = value.trim();
  // Remove thousands separators (comma or space) and check
  const cleaned = trimmed.replace(/[, ]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return false;
  return /^-?\d+(\.\d+)?$/.test(cleaned);
}


type TypeDetector = {
  type: ColumnType;
  detect: (value: string) => boolean;
};

// Priority order: most specific first, Text is always fallback
// Number before Boolean so columns with diverse numbers aren't misclassified.
// Boolean only wins if 60%+ of values are strictly boolean-like (true/false/yes/no/1/0).
const DETECTORS: TypeDetector[] = [
  { type: 'URL', detect: isURL },
  { type: 'DateTime', detect: isDateTime },
  { type: 'Date', detect: isDate },
  { type: 'Percentage', detect: isPercentage },
  { type: 'Currency', detect: isCurrency },
  { type: 'Number', detect: isNumber },
  { type: 'Boolean', detect: isBoolean },
];

/**
 * Infer the type of a single column from its values and optionally its name.
 * Samples up to MAX_SAMPLE_SIZE non-null values.
 * Column name is used as context to help disambiguate close calls.
 * Returns 'Text' if no specific type matches the majority.
 */
export function inferColumnType(values: (string | number | null)[], columnName?: string): ColumnType {
  // Filter non-null, non-empty values
  const nonNull = values
    .filter((v): v is string | number => v != null && v !== '')
    .map((v) => String(v));

  // All null/empty → default to Text
  if (nonNull.length === 0) return 'Text';

  // Sample if too large
  const sample =
    nonNull.length <= MAX_SAMPLE_SIZE
      ? nonNull
      : nonNull.slice(0, MAX_SAMPLE_SIZE);

  // Count matches for each detector (a value can match multiple detectors)
  const counts: Record<ColumnType, number> = {
    Text: 0,
    Number: 0,
    Date: 0,
    DateTime: 0,
    Percentage: 0,
    Currency: 0,
    Boolean: 0,
    URL: 0,
  };

  for (const value of sample) {
    let matched = false;
    for (const detector of DETECTORS) {
      if (detector.detect(value)) {
        counts[detector.type]++;
        matched = true;
      }
    }
    if (!matched) {
      counts['Text']++;
    }
  }

  // Threshold: a type must match at least 60% of values to be inferred
  const threshold = sample.length * 0.6;
  // Lower threshold when column name strongly hints at a type
  const nameHint = columnName ? getNameHint(columnName) : null;
  const hintedThreshold = sample.length * 0.4;

  // If Boolean matches 100% of values AND Number also matches (i.e. all values are 1/0),
  // prefer Boolean since the column is exclusively binary.
  if (
    counts['Boolean'] >= threshold &&
    counts['Boolean'] === sample.length &&
    counts['Number'] >= threshold
  ) {
    return 'Boolean';
  }

  // If column name hints at Boolean and all values are 1/0, prefer Boolean
  if (
    nameHint === 'Boolean' &&
    counts['Boolean'] === sample.length
  ) {
    return 'Boolean';
  }

  // Pick the best type following priority order
  let bestType: ColumnType = 'Text';
  let bestCount = 0;

  for (const detector of DETECTORS) {
    // Use lower threshold if column name hints at this type
    const effectiveThreshold = (nameHint === detector.type) ? hintedThreshold : threshold;
    if (counts[detector.type] > bestCount && counts[detector.type] >= effectiveThreshold) {
      bestCount = counts[detector.type];
      bestType = detector.type;
    }
  }

  // If no type passed the threshold but column name gives a strong hint
  // and at least some values match, use the hint
  if (bestType === 'Text' && nameHint && counts[nameHint] > 0) {
    return nameHint;
  }

  return bestType;
}

/**
 * Infer types for multiple columns.
 * Each column is an array of cell values.
 * Column names are used as context to help disambiguation.
 */
export function inferColumnTypes(
  columns: (string | number | null)[][],
  columnNames?: string[]
): ColumnType[] {
  return columns.map((col, idx) => inferColumnType(col, columnNames?.[idx]));
}

/**
 * Extract column value arrays from dataset rows.
 * Transforms row-oriented data into column-oriented data.
 */
export function extractColumnsFromRows(
  rows: { values: (string | number | null)[] }[],
  columnCount: number
): (string | number | null)[][] {
  const columns: (string | number | null)[][] = Array.from(
    { length: columnCount },
    () => []
  );

  for (const row of rows) {
    for (let i = 0; i < columnCount; i++) {
      columns[i].push(row.values[i] ?? null);
    }
  }

  return columns;
}
